import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  AlertTriangle, Cloud, HardDrive, GitMerge, 
  Clock, FileText, ChevronDown, ChevronUp 
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function SyncConflictDialog({ 
  conflict, 
  onResolve, 
  onClose 
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [resolving, setResolving] = useState(false);

  if (!conflict) return null;

  const { localNote, serverNote, noteTitle } = conflict;

  const handleResolve = async (choice) => {
    setResolving(true);
    try {
      await onResolve(choice);
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  const getWordCount = (content) => {
    return content?.split(/\s+/).filter(Boolean).length || 0;
  };

  return (
    <AlertDialog open={!!conflict} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-xl" data-testid="sync-conflict-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-accent-amber">
            <AlertTriangle className="w-5 h-5" />
            Sync Conflict Detected
          </AlertDialogTitle>
          <AlertDialogDescription>
            The note "<span className="font-medium text-foreground">{noteTitle || 'Untitled'}</span>" 
            was modified both locally and on the server. Choose which version to keep.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Version Comparison Cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Local Version */}
            <div className="p-3 bg-secondary/50 border border-border rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium">Local Version</span>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  <span>Modified {formatDate(localNote.updated_at)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3 h-3" />
                  <span>{getWordCount(localNote.content)} words</span>
                </div>
              </div>
              {localNote.is_offline && (
                <Badge variant="secondary" className="text-[10px]">Offline edit</Badge>
              )}
            </div>

            {/* Server Version */}
            <div className="p-3 bg-secondary/50 border border-border rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium">Server Version</span>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  <span>Modified {formatDate(serverNote.updated_at)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <FileText className="w-3 h-3" />
                  <span>{getWordCount(serverNote.content)} words</span>
                </div>
              </div>
            </div>
          </div>

          {/* Show Diff Toggle */}
          <button
            onClick={() => setShowDiff(!showDiff)}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="toggle-diff-btn"
          >
            {showDiff ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDiff ? 'Hide content preview' : 'Show content preview'}
          </button>

          {/* Content Preview */}
          {showDiff && (
            <div className="grid grid-cols-2 gap-3">
              <ScrollArea className="h-32 rounded-md border border-border bg-background p-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
                  {localNote.content?.slice(0, 500) || '(empty)'}
                  {localNote.content?.length > 500 && '...'}
                </pre>
              </ScrollArea>
              <ScrollArea className="h-32 rounded-md border border-border bg-background p-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
                  {serverNote.content?.slice(0, 500) || '(empty)'}
                  {serverNote.content?.length > 500 && '...'}
                </pre>
              </ScrollArea>
            </div>
          )}
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => handleResolve('local')}
            disabled={resolving}
            className="flex-1"
            data-testid="keep-local-btn"
          >
            <HardDrive className="w-4 h-4 mr-2" />
            Keep Local
          </Button>
          <Button
            variant="outline"
            onClick={() => handleResolve('server')}
            disabled={resolving}
            className="flex-1"
            data-testid="keep-server-btn"
          >
            <Cloud className="w-4 h-4 mr-2" />
            Keep Server
          </Button>
          <Button
            onClick={() => handleResolve('both')}
            disabled={resolving}
            className="flex-1"
            data-testid="keep-both-btn"
          >
            <GitMerge className="w-4 h-4 mr-2" />
            Keep Both
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
